import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { wishlistService } from '@/services/wishlist.service';
import type {
  WishlistItem,
  WishlistResponse,
  AllWishlistsResponse,
  MyReservedItemsResponse,
  CreateWishlistItemDto,
  UpdateWishlistItemDto,
  WishlistFilters,
  UserBasic,
} from '@/types/gift.types';
import { ApiError } from '@/services/api';

// Wishlist state interface
interface WishlistState {
  // My wishlist
  myWishlist: WishlistItem[];
  myWishlistUser: UserBasic | null;
  // Viewing another user's wishlist
  viewingWishlist: WishlistItem[];
  viewingUser: UserBasic | null;
  // All wishlists for browsing
  allWishlists: AllWishlistsResponse['wishlists'];
  // Items I've reserved
  reservedItems: WishlistItem[];
  // Filters
  filters: WishlistFilters;
  // Loading states
  isLoading: boolean;
  isLoadingMy: boolean;
  isLoadingUser: boolean;
  isLoadingAll: boolean;
  isLoadingReserved: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isReserving: boolean;
  // Error
  error: string | null;
}

// Initial state
const initialState: WishlistState = {
  myWishlist: [],
  myWishlistUser: null,
  viewingWishlist: [],
  viewingUser: null,
  allWishlists: [],
  reservedItems: [],
  filters: {
    page: 1,
    limit: 50,
    sortBy: 'priority',
    sortOrder: 'desc',
  },
  isLoading: false,
  isLoadingMy: false,
  isLoadingUser: false,
  isLoadingAll: false,
  isLoadingReserved: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isReserving: false,
  error: null,
};

// Async thunks

// Fetch my wishlist
export const fetchMyWishlist = createAsyncThunk<
  WishlistResponse,
  WishlistFilters | undefined,
  { rejectValue: string }
>('wishlist/fetchMy', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { wishlist: WishlistState };
    const mergedFilters = { ...state.wishlist.filters, ...filters };
    return await wishlistService.getMyWishlist(mergedFilters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Wunschliste');
  }
});

// Fetch user's wishlist
export const fetchUserWishlist = createAsyncThunk<
  WishlistResponse,
  { userId: string; filters?: WishlistFilters },
  { rejectValue: string }
>('wishlist/fetchUser', async ({ userId, filters }, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { wishlist: WishlistState };
    const mergedFilters = { ...state.wishlist.filters, ...filters };
    return await wishlistService.getUserWishlist(userId, mergedFilters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Wunschliste');
  }
});

// Fetch all wishlists
export const fetchAllWishlists = createAsyncThunk<
  AllWishlistsResponse,
  void,
  { rejectValue: string }
>('wishlist/fetchAll', async (_, { rejectWithValue }) => {
  try {
    return await wishlistService.getAllWishlists();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Wunschlisten');
  }
});

// Fetch my reserved items - REMOVED: Backend doesn't have separate endpoint
// Reserved items are included in getMyWishlist response
// export const fetchMyReservedItems = createAsyncThunk<
//   MyReservedItemsResponse,
//   void,
//   { rejectValue: string }
// >('wishlist/fetchReserved', async (_, { rejectWithValue }) => {
//   try {
//     return await wishlistService.getMyReservedItems();
//   } catch (error) {
//     if (error instanceof ApiError) {
//       return rejectWithValue(error.message);
//     }
//     return rejectWithValue('Fehler beim Laden der reservierten Items');
//   }
// });

// Create wishlist item
export const createWishlistItem = createAsyncThunk<
  WishlistItem,
  CreateWishlistItemDto,
  { rejectValue: string }
>('wishlist/create', async (data, { rejectWithValue }) => {
  try {
    return await wishlistService.createWishlistItem(data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen des Wunsches');
  }
});

// Update wishlist item
export const updateWishlistItem = createAsyncThunk<
  WishlistItem,
  { itemId: string; data: UpdateWishlistItemDto },
  { rejectValue: string }
>('wishlist/update', async ({ itemId, data }, { rejectWithValue }) => {
  try {
    return await wishlistService.updateWishlistItem(itemId, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren des Wunsches');
  }
});

// Delete wishlist item
export const deleteWishlistItem = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('wishlist/delete', async (itemId, { rejectWithValue }) => {
  try {
    await wishlistService.deleteWishlistItem(itemId);
    return itemId;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Löschen des Wunsches');
  }
});

// Reserve item
export const reserveWishlistItem = createAsyncThunk<
  WishlistItem,
  string,
  { rejectValue: string }
>('wishlist/reserve', async (itemId, { rejectWithValue }) => {
  try {
    return await wishlistService.reserveItem(itemId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Reservieren');
  }
});

// Unreserve item
export const unreserveWishlistItem = createAsyncThunk<
  WishlistItem,
  string,
  { rejectValue: string }
>('wishlist/unreserve', async (itemId, { rejectWithValue }) => {
  try {
    return await wishlistService.unreserveItem(itemId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aufheben der Reservierung');
  }
});

// Mark as purchased
export const markItemPurchased = createAsyncThunk<
  WishlistItem,
  string,
  { rejectValue: string }
>('wishlist/markPurchased', async (itemId, { rejectWithValue }) => {
  try {
    return await wishlistService.markAsPurchased(itemId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Markieren als gekauft');
  }
});

// Wishlist slice
const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearViewingWishlist: (state) => {
      state.viewingWishlist = [];
      state.viewingUser = null;
    },
    setFilters: (state, action: PayloadAction<Partial<WishlistFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 50,
        sortBy: 'priority',
        sortOrder: 'desc',
      };
    },
    resetWishlist: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch my wishlist
    builder
      .addCase(fetchMyWishlist.pending, (state) => {
        state.isLoadingMy = true;
        state.error = null;
      })
      .addCase(fetchMyWishlist.fulfilled, (state, action) => {
        state.isLoadingMy = false;
        state.myWishlist = action.payload.items;
        state.myWishlistUser = action.payload.user;
      })
      .addCase(fetchMyWishlist.rejected, (state, action) => {
        state.isLoadingMy = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch user wishlist
    builder
      .addCase(fetchUserWishlist.pending, (state) => {
        state.isLoadingUser = true;
        state.error = null;
      })
      .addCase(fetchUserWishlist.fulfilled, (state, action) => {
        state.isLoadingUser = false;
        state.viewingWishlist = action.payload.items;
        state.viewingUser = action.payload.user;
      })
      .addCase(fetchUserWishlist.rejected, (state, action) => {
        state.isLoadingUser = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch all wishlists
    builder
      .addCase(fetchAllWishlists.pending, (state) => {
        state.isLoadingAll = true;
        state.error = null;
      })
      .addCase(fetchAllWishlists.fulfilled, (state, action) => {
        state.isLoadingAll = false;
        state.allWishlists = action.payload.wishlists;
      })
      .addCase(fetchAllWishlists.rejected, (state, action) => {
        state.isLoadingAll = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch reserved items - REMOVED: Backend doesn't have separate endpoint
    // Reserved items are included in getMyWishlist response
    // builder
    //   .addCase(fetchMyReservedItems.pending, (state) => {
    //     state.isLoadingReserved = true;
    //     state.error = null;
    //   })
    //   .addCase(fetchMyReservedItems.fulfilled, (state, action) => {
    //     state.isLoadingReserved = false;
    //     state.reservedItems = action.payload.items;
    //   })
    //   .addCase(fetchMyReservedItems.rejected, (state, action) => {
    //     state.isLoadingReserved = false;
    //     state.error = action.payload ?? 'Fehler beim Laden';
    //   });

    // Create item
    builder
      .addCase(createWishlistItem.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createWishlistItem.fulfilled, (state, action) => {
        state.isCreating = false;
        state.myWishlist.unshift(action.payload);
      })
      .addCase(createWishlistItem.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? 'Fehler beim Erstellen';
      });

    // Update item
    builder
      .addCase(updateWishlistItem.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateWishlistItem.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.myWishlist.findIndex((i) => i.id === action.payload.id);
        if (index !== -1) {
          state.myWishlist[index] = action.payload;
        }
      })
      .addCase(updateWishlistItem.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? 'Fehler beim Aktualisieren';
      });

    // Delete item
    builder
      .addCase(deleteWishlistItem.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteWishlistItem.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.myWishlist = state.myWishlist.filter((i) => i.id !== action.payload);
      })
      .addCase(deleteWishlistItem.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? 'Fehler beim Löschen';
      });

    // Reserve item
    builder
      .addCase(reserveWishlistItem.pending, (state) => {
        state.isReserving = true;
        state.error = null;
      })
      .addCase(reserveWishlistItem.fulfilled, (state, action) => {
        state.isReserving = false;
        // Update in viewing wishlist
        const viewIndex = state.viewingWishlist.findIndex((i) => i.id === action.payload.id);
        if (viewIndex !== -1) {
          state.viewingWishlist[viewIndex] = action.payload;
        }
        // Add to reserved items
        if (!state.reservedItems.find((i) => i.id === action.payload.id)) {
          state.reservedItems.push(action.payload);
        }
        // Update in all wishlists
        state.allWishlists = state.allWishlists.map((wl) => ({
          ...wl,
          items: wl.items.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
          topItems: wl.topItems.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
        }));
      })
      .addCase(reserveWishlistItem.rejected, (state, action) => {
        state.isReserving = false;
        state.error = action.payload ?? 'Fehler beim Reservieren';
      });

    // Unreserve item
    builder
      .addCase(unreserveWishlistItem.pending, (state) => {
        state.isReserving = true;
        state.error = null;
      })
      .addCase(unreserveWishlistItem.fulfilled, (state, action) => {
        state.isReserving = false;
        // Update in viewing wishlist
        const viewIndex = state.viewingWishlist.findIndex((i) => i.id === action.payload.id);
        if (viewIndex !== -1) {
          state.viewingWishlist[viewIndex] = action.payload;
        }
        // Remove from reserved items
        state.reservedItems = state.reservedItems.filter((i) => i.id !== action.payload.id);
        // Update in all wishlists
        state.allWishlists = state.allWishlists.map((wl) => ({
          ...wl,
          items: wl.items.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
          topItems: wl.topItems.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
        }));
      })
      .addCase(unreserveWishlistItem.rejected, (state, action) => {
        state.isReserving = false;
        state.error = action.payload ?? 'Fehler beim Aufheben';
      });

    // Mark purchased
    builder
      .addCase(markItemPurchased.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(markItemPurchased.fulfilled, (state, action) => {
        state.isUpdating = false;
        // Update in reserved items
        const reservedIndex = state.reservedItems.findIndex((i) => i.id === action.payload.id);
        if (reservedIndex !== -1) {
          state.reservedItems[reservedIndex] = action.payload;
        }
        // Update in viewing wishlist
        const viewIndex = state.viewingWishlist.findIndex((i) => i.id === action.payload.id);
        if (viewIndex !== -1) {
          state.viewingWishlist[viewIndex] = action.payload;
        }
      })
      .addCase(markItemPurchased.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? 'Fehler beim Markieren';
      });
  },
});

// Export actions
export const {
  clearError,
  clearViewingWishlist,
  setFilters,
  resetFilters,
  resetWishlist,
} = wishlistSlice.actions;

// Selectors
export const selectMyWishlist = (state: { wishlist: WishlistState }) => state.wishlist.myWishlist;
export const selectMyWishlistUser = (state: { wishlist: WishlistState }) => state.wishlist.myWishlistUser;
export const selectViewingWishlist = (state: { wishlist: WishlistState }) => state.wishlist.viewingWishlist;
export const selectViewingUser = (state: { wishlist: WishlistState }) => state.wishlist.viewingUser;
export const selectAllWishlists = (state: { wishlist: WishlistState }) => state.wishlist.allWishlists;
export const selectReservedItems = (state: { wishlist: WishlistState }) => state.wishlist.reservedItems;
export const selectWishlistFilters = (state: { wishlist: WishlistState }) => state.wishlist.filters;
export const selectWishlistLoading = (state: { wishlist: WishlistState }) => ({
  isLoading: state.wishlist.isLoading,
  isLoadingMy: state.wishlist.isLoadingMy,
  isLoadingUser: state.wishlist.isLoadingUser,
  isLoadingAll: state.wishlist.isLoadingAll,
  isLoadingReserved: state.wishlist.isLoadingReserved,
  isCreating: state.wishlist.isCreating,
  isUpdating: state.wishlist.isUpdating,
  isDeleting: state.wishlist.isDeleting,
  isReserving: state.wishlist.isReserving,
});
export const selectWishlistError = (state: { wishlist: WishlistState }) => state.wishlist.error;

// Export reducer
export default wishlistSlice.reducer;
